import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'motion/react';
import { DefaultProfileIcon } from 'assets/svgs';
import { OnlineIndicator } from 'components';

function Contributor({
  contributor,
  comments,
  totalQuestions,
  onlineUsers,
  sessionId
}) {
  const [completion, setCompletion] = useState(null);
  const [status, setStatus] = useState({
    name: 'Contributing',
    value: 'contributing',
    primaryColor: 'var(--light-blue)',
    secondaryColor: 'var(--blue)'
  });

  useEffect(() => {
    const includesCommentsFromContributor = comments.some(
      (comment) => comment.uid === contributor.id
    );

    if (!includesCommentsFromContributor) {
      setStatus({
        name: 'No Contribution Yet',
        value: 'no-contribution-yet',
        primaryColor: 'var(--light-grey)',
        secondaryColor: 'var(--light-brown)'
      });
    }

    // Get unique questions answered by counting distinct questionIds
    const uniqueQuestionsAnswered = new Set(
      comments
        .filter((comment) => comment.uid === contributor.id)
        .map((comment) => comment.questionId)
    ).size;

    const completionPercentage = Math.min(
      (uniqueQuestionsAnswered / totalQuestions) * 100,
      100
    );

    setCompletion(completionPercentage);
  }, [comments]);

  return (
    <ContributorContainer>
      <ProfilePictureContainer>
        {contributor?.avatarUrl ? (
          <ProfilePicture src={contributor.avatarUrl} alt={contributor.name} />
        ) : (
          <DefaultProfileIcon
            primaryColor={status.primaryColor}
            secondaryColor={status.secondaryColor}
          />
        )}
        <OnlineIndicatorContainer>
          <OnlineIndicator
            isOnline={onlineUsers?.some((u) => u.id === contributor.id)}
            size="12px"
          />
        </OnlineIndicatorContainer>
      </ProfilePictureContainer>
      <ContributorInfo>
        <p>
          {contributor.name}
          <span>{contributor?.country}</span>
        </p>
        <CompletionIndication>
          <h5>Session Completed:</h5>
          <div>
            <span>{completion ? `${parseInt(completion, 10)}%` : '0%'}</span>
            <IndicationBar>
              <Indicator
                style={{
                  width: `${parseInt(completion, 10)}%`,
                  display: completion > 0 ? 'block' : 'none',
                  transition: 'width 1.5s ease-out'
                }}
              />
              <Increments>
                <IndicationIncrement />
                <IndicationIncrement />
                <IndicationIncrement />
                <IndicationIncrement />
                <IndicationIncrement />
                <IndicationIncrement />
                <IndicationIncrement />
                <IndicationIncrement />
                <IndicationIncrement />
              </Increments>
            </IndicationBar>
          </div>
        </CompletionIndication>
      </ContributorInfo>
    </ContributorContainer>
  );
}

const ContributorContainer = styled.div`
  color: var(--brown);
  display: flex;
  gap: 0.5rem;
`;

const ContributorInfo = styled.div`
  display: flex;
  flex-direction: column;
  /* gap: 0.25rem; */
  p {
    display: flex;
    flex-direction: column;
    font-size: 1rem;
    gap: 0.25rem;
    span {
      color: var(--light-brown);
      font-size: 0.875rem;
    }
  }
`;

const ProfilePictureContainer = styled.div`
  border-radius: 50%;
  height: 2.875rem;
  overflow: visible;
  position: relative;
  width: 2.875rem;
`;

const ProfilePicture = styled(motion.img).attrs({
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1
  },
  exit: {
    opacity: 0
  }
})`
  background: transparent;
  border-radius: 50%;
  object-fit: cover;
  width: 100%;
  height: 100%;
`;

const CompletionIndication = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  h5 {
    color: var(--brown);
    font-family: 'Inter', sans-serif;
    font-size: 0.875rem;
    font-weight: 600;
  }

  > div {
    align-items: center;
    display: flex;
    gap: 0.5rem;
    span {
      color: var(--blue);
      font-size: 0.875rem;
      font-weight: 700;
    }
  }
`;

const IndicationBar = styled.div`
  background-color: var(--light-grey);
  border: 1px solid var(--blue);
  border-radius: 100px;
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  height: 12px;
  position: relative;
  width: 120px;
`;

const Increments = styled.div`
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 2;
`;

const IndicationIncrement = styled.div`
  border-right: 1px solid var(--blue);
`;

const Indicator = styled.div`
  background-color: var(--dark-blue);
  border-radius: 100px;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 1;
`;

const OnlineIndicatorContainer = styled.div`
  position: absolute;
  right: -2px;
  top: -2px;
  z-index: 10;
`;

export default Contributor;
